import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../components/user/AuthProvider';
import { useNavigate, useLocation } from 'react-router-dom';
import { useRoleLinks } from '../../hooks/useNav';
import { useTranslation } from "react-i18next";
import { FiChevronDown } from "react-icons/fi";
import MenuLink from './MenuLink';

const HeaderLinks = ({ profileInfo }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeIndex, setActiveIndex] = useState(null);
  // Index of the parent item whose child menu is open (null = none).
  const [openIndex, setOpenIndex] = useState(null);
  const containerRef = useRef(null);

  const { items: subMenu } = useRoleLinks(profileInfo?.role);
  const { t } = useTranslation();

   // Set active index based on the current URL path on component mount
   useEffect(() => {
    const currentPath = location.pathname;
    const initialIndex = subMenu.findIndex(item =>
      item.path === currentPath ||
      (item.children || []).some(child => currentPath.startsWith(child.path)));
    if (initialIndex !== -1) {
      setActiveIndex(initialIndex);
    }else{
      setActiveIndex(-1);
    }
  }, [location.pathname, subMenu]);

  // Close the open child menu on outside click / route change.
  useEffect(() => setOpenIndex(null), [location.pathname]);
  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpenIndex(null);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleLinkClick = (index) => {
    setActiveIndex(index);
    setOpenIndex(null);
  };

  const linkStyle = (index) => ({
    display: 'inline-block',
    paddingBottom: '5px',  // Space for the underline
    borderBottom: activeIndex === index ? '2px solid black' : 'none',  // Active underline
    textDecoration: 'none',  // Remove default underline
    color: 'black',  // Link color
    fontWeight: activeIndex === index ? 'bold' : 'normal',
  });

  return (
    <span ref={containerRef} className="flex items-center gap-4">
      {subMenu.map((item, index) => (
        item.children ? (
          <span key={index} className="relative">
            <button
              type="button"
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
              style={{ ...linkStyle(index), background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <span className="flex items-center gap-1">
                {t(item.label)}
                <FiChevronDown
                  className={`transition-transform duration-200 ${openIndex === index ? 'rotate-180' : ''}`}
                />
              </span>
            </button>
            {openIndex === index && (
              <div className="absolute right-0 top-full z-50 mt-2 min-w-[210px] rounded-md border border-gray-200 bg-white py-1 shadow-lg">
                {item.children.map((child, childIndex) => (
                  <MenuLink
                    key={`${index}_${childIndex}`}
                    item={child}
                    onClick={() => handleLinkClick(index)}
                    className={`block px-4 py-2 text-sm hover:bg-gray-100 ${
                      location.pathname.startsWith(child.path) ? 'font-bold' : ''
                    }`}
                  >
                    {t(child.label)}
                  </MenuLink>
                ))}
              </div>
            )}
          </span>
        ) : (
          <MenuLink
            key={index}
            item={item}
            onClick={() => handleLinkClick(index)} // Set active index on click
            style={linkStyle(index)}
          >
            <span className="flex items-center">
            {t(item.label)}
            </span>
          </MenuLink>
        )
    ))}
    </span>
  );
};

export default HeaderLinks;
